import { FC } from "react";
import Markdown from "react-markdown";

import "./SATOSContentRender.css";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

import { insertDomain } from "@/service/ApplicationQueries";

// CSS properties that can be abused to overlay, reposition, or hide UI elements.
// We strip these from inline styles while keeping visual properties (colors, borders, etc.).
const BLOCKED_CSS_PROPS = new Set([
	"position",
	"top", "left", "right", "bottom",
	"z-index",
	"transform",
	"clip", "clip-path",
	"pointer-events",
	"float",
]);

// Table elements whose background-color is fully controlled by theme CSS —
// strip inline background-color so dark/light mode CSS rules apply cleanly.
const TABLE_ELEMENTS = new Set(["table", "thead", "tbody", "tfoot", "tr", "td", "th"]);

function sanitizeCssValue(prop: string, value: string, tagName?: string): boolean {
	const p = prop.trim().toLowerCase();
	const v = value.trim().toLowerCase();
	if (BLOCKED_CSS_PROPS.has(p)) return false;
	// Strip background-color from table elements — theme CSS controls all table colors.
	if (p === "background-color" && tagName && TABLE_ELEMENTS.has(tagName)) return false;
	if (p === "background" && tagName && TABLE_ELEMENTS.has(tagName)) return false;
	if (p === "display" && v === "none") return false;
	if (p === "visibility" && v !== "visible") return false;
	if (p === "opacity" && parseFloat(v) === 0) return false;
	return true;
}

function stripDangerousInlineStyles(style: string, tagName?: string): string | undefined {
	const result = style
		.split(";")
		.filter((decl) => {
			const colon = decl.indexOf(":");
			if (colon < 0) return false;
			return sanitizeCssValue(decl.slice(0, colon), decl.slice(colon + 1), tagName);
		})
		.join(";")
		.trim();
	return result || undefined;
}

// Rehype plugin: walk the hast tree and sanitize inline style values.
const rehypeStripDangerousCss = () => (tree: any) => {
	const walk = (node: any) => {
		if (node.type === "element" && typeof node.properties?.style === "string") {
			const cleaned = stripDangerousInlineStyles(node.properties.style, node.tagName as string);
			if (cleaned) {
				node.properties.style = cleaned;
			} else {
				delete node.properties.style;
			}
		}
		node.children?.forEach(walk);
	};
	walk(tree);
};

interface IMarkdowContentRenderer {
	className?: string;
	isNotProcedureRender?: boolean;
	content: string | undefined;
	rehypePlugins?: Markdown.PluggableList | undefined;
	insertDomainOptions?: { insertIntoHtmlElement: boolean };
	linkTarget?: string;
	OnClickImage?: () => void;
}

export const SATOSContentRender: FC<IMarkdowContentRenderer> = (props) => {
	const customSchema = {
		...defaultSchema,
		tagNames: [...(defaultSchema.tagNames || []), "u", "td"], // Add <u> tag
		attributes: {
			...(defaultSchema.attributes || {}),
			div: [
				...(defaultSchema.attributes?.div || []),
				"style",
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
			span: [
				...(defaultSchema.attributes?.span || []),
				"style",
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
			img: [
				...(defaultSchema.attributes?.img || []),
				"style",
				"width",
				"height",
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
			table: [
				...(defaultSchema.attributes?.table || []),
				"style",
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
			thead: [
				...(defaultSchema.attributes?.thead || []),
				"style",
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
			tbody: [
				...(defaultSchema.attributes?.tbody || []),
				"style",
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
			tfoot: [
				...(defaultSchema.attributes?.tfoot || []),
				"style",
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
			tr: [
				...(defaultSchema.attributes?.tr || []),
				"style",
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
			td: [
				...(defaultSchema.attributes?.td || []),
				"style",
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
			th: [
				...(defaultSchema.attributes?.th || []),
				"style",
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
			u: [
				...(defaultSchema.attributes?.u || []),
				["className", "vdd-added", "vdd-removed", "vdd-modified"],
				["id", "vdd-added", "vdd-removed", "vdd-modified"],
			],
		},
	};

	const basePlugins = [rehypeRaw, [rehypeSanitize, customSchema], [rehypeStripDangerousCss]] as Markdown.PluggableList;
	const plugins: Markdown.PluggableList = props.rehypePlugins
		? [...props.rehypePlugins, ...basePlugins]
		: basePlugins;

	const options = props.insertDomainOptions ?? {
		insertIntoHtmlElement: true,
		markdown: true,
	};

	const linkTarget = props.linkTarget ? props.linkTarget : "_blank";

	if (!props.content) {
		return <></>;
	}

	return (
		<Markdown
			className={
				props.isNotProcedureRender
					? props.className
						? `satos-content ${props.className}`
						: "satos-content"
					: props.className
			}
			rehypePlugins={plugins}
			linkTarget={linkTarget}
		>
			{insertDomain(props.content, options)}
		</Markdown>
	);
};
